import { Component, DoCheck, effect, inject, input, model, viewChildren } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ExportCsvService } from 'src/app/services/export-csv.service';
import { CsvModes } from 'src/app/core/csv-modes.class';
import { DateFormatPipe } from 'src/app/pipes/date-format.pipe';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { DealHierarchy } from 'src/app/models/classes/deal-hierarchy.class';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { CommonModule } from '@angular/common';
import { ExpandedViewSliderComponent } from 'src/app/shared/expanded-view-slider/expanded-view-slider.component';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { TvpiValueFormatPipe } from 'src/app/pipes/tvpi-value-format.pipe';
import { IrrValueFormatPipe } from 'src/app/pipes/irr-value-format.pipe';
import { SortByPipe } from 'src/app/pipes/sortBy.pipe';
import { InfoIconComponent } from 'src/app/shared/info-icon/info-icon.component';
import { MatIconModule } from '@angular/material/icon';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { MatSortModule} from '@angular/material/sort';
import { InvestmentTypeHierarchy } from 'src/app/models/classes/investment-type-hierarchy.class';
import { AggregatedPortfolioSeries } from 'src/app/models/classes/aggregated-portfolio-series.class';
import { PortfolioSeries } from 'src/app/models/classes/portfolio-series.class';
import { SeriesTableConfigService } from '../../services/series-table-config.service';
import { TableLogicService } from 'src/app/services/table-logic.service';

@Component({
    selector: 'app-series-deals-table',
    imports: [
        CommonModule,
        ExpandedViewSliderComponent,
        MatTableModule,
        CurrencyFormatPipe,
        TvpiValueFormatPipe,
        IrrValueFormatPipe,
        SortByPipe,
        RouterModule,
        InfoIconComponent,
        DateFormatPipe,
        MatIconModule,
        TourMatMenu,
        MatSortModule
    ],
    providers: [TvpiValueFormatPipe, IrrValueFormatPipe],
    templateUrl: './series-deals-table.component.html',
    styleUrls: ['./series-deals-table.component.scss']
})
export class SeriesDealsTableComponent implements DoCheck {
  private exportCsvService = inject(ExportCsvService);
  private dateFormatPipe = inject(DateFormatPipe);
  private tvpiValueFormatPipe = inject(TvpiValueFormatPipe);
  private irrValueFormatPipe = inject(IrrValueFormatPipe);
  activityTracking = inject(ActivityTrackingService);
  private seriesTableConfig = inject(SeriesTableConfigService);
  private tableLogicService = inject(TableLogicService);
  protected readonly tableConfig = this.seriesTableConfig.config;

  readonly iconSet = input<Record<string, string> | undefined>(undefined);
  readonly investmentTypes = model<InvestmentTypeHierarchy[]>([]);
  readonly summary = input<AggregatedPortfolioSeries | PortfolioSeries | undefined>(undefined);
  readonly portfolioName = input<string>('');
  readonly seriesName = input<string>('');
  readonly currency = input<string | undefined>(undefined);
  readonly investorId = input<number | undefined>(undefined);
  readonly seriesId = input<number | undefined>(undefined);
  readonly reportingPeriod = input<string | undefined>(undefined);
  readonly isAggregatedSeries = input<boolean>(false);
  expandView = false;
  columnsToDisplay: string[] = [];
  readonly sorts = viewChildren(MatSort);

  dataSources: MatTableDataSource<DealHierarchy>[] = []
  sortingInitialized = false;
  isExpandedSeries = true;

  constructor() {
    effect(() => {
      const cfg = this.seriesTableConfig.config();
      this.columnsToDisplay = this.expandView ? cfg.deals.expandedColumns : cfg.deals.columns;
    });

    effect(() => {
      if (this.investmentTypes() && this.investmentTypes().length > 0) {
        this.formatTableValues();
        this.sortingInitialized = false;
      }
    });
  }

  formatTableValues() {
    this.investmentTypes.set(this.tableLogicService.sortDataForSeriesTable(this.investmentTypes()));
    this.investmentTypes().forEach((investmentType) => {
      investmentType['isExpanded'] = true;
      this.dataSources[(investmentType['priority'] ?? 1) - 1] = new MatTableDataSource(investmentType.deals);
      investmentType.deals.forEach(deal => {
        deal.showHoldings = this.tableLogicService.checkDealToHoldingMapping(deal.holdings);
        deal.disableDrillThrough = this.tableLogicService.isDealDrillThroughDisabled(deal);
      })
    })
  }

  ngDoCheck() {
    this.initializeSorting();
  }

  initializeSorting() {
    if (!this.sortingInitialized) {
      if (this.sorts().length > 0) {
        this.dataSources.forEach((x, index) => {
          x.sort = this.sorts().find((_, i) => i == index)
        });
        this.sortingInitialized = true;
      }
    }
  }

  exportSeriesDealsDataToCsv(event: MouseEvent) {
    const seriesName = this.seriesName();
    this.activityTracking.exportToExcelClick('series deals table', seriesName);
    (event.target as HTMLButtonElement).disabled = true;
    const cfg = this.seriesTableConfig.config();
    const seriesDealsArray = [], seriesHoldingsArray = [], invTypeArray = [];
    const filename = this.exportCsvService.getExportFilename(CsvModes.seriesDeals, this.reportingPeriod() ?? '', this.portfolioName(), seriesName);
    const headerForSeriesDeals = this.exportCsvService.getCsvHeader(CsvModes.seriesDeals, cfg.classification.headerLabel);
    const headerForSeriesHoldings = this.exportCsvService.getCsvHeader(CsvModes.seriesHoldings, cfg.classification.headerLabel);
    const { invTypeTvpi, invTypeIrr, ...invTypeHeaderWithoutPerformance } = this.exportCsvService.getCsvHeader(CsvModes.InvestmentType);
    const headerForInvestmentType = cfg.showSubtotalPerformance
      ? { ...invTypeHeaderWithoutPerformance, invTypeTvpi, invTypeIrr }
      : invTypeHeaderWithoutPerformance;
    seriesDealsArray.push(headerForSeriesDeals);
    seriesHoldingsArray.push(headerForSeriesHoldings);
    invTypeArray.push(headerForInvestmentType);
    this.investmentTypes().forEach(invType => {
      const invTypeData: Record<string, unknown> = {};
      invTypeData['invType'] = invType.name;
      invTypeData['invTypeCommitment'] = invType.commitment;
      invTypeData['invTypeReportedContributions'] = invType.reportedContributions;
      invTypeData['invTypeContributions'] = invType.contributions;
      invTypeData['invTypeRemainingCommitment'] = invType.remainingCommitment;
      invTypeData['invTypeReportedDistributions'] = invType.reportedDistributions;
      invTypeData['invTypeDistributions'] = invType.distributions;
      invTypeData['invTypeReportedValue'] = invType.reportedValue;
      invTypeData['invTypeSubsequentContributions'] = invType.subsequentContributions;
      invTypeData['invTypeSubsequentDistributions'] = invType.subsequentDistributions;
      invTypeData['invTypeFxGainLoss'] = invType.fxGainLoss;
      invTypeData['invTypeNav'] = invType.nav;
      invTypeData['invTypeTotalValue'] = invType.totalValue;
      if (cfg.showSubtotalPerformance) {
        invTypeData['invTypeTvpi'] = this.tvpiValueFormatPipe.transform(invType.tvpi);
        invTypeData['invTypeIrr'] = this.irrValueFormatPipe.transform(invType.irr);
      }
      invTypeArray.push(invTypeData);

      const sortedDeals = this.exportCsvService.sortArrayAscending(invType.deals);
      sortedDeals.forEach((deal: DealHierarchy) => {
        const dealData: Record<string, unknown> = {};
        dealData['invType'] = invType.name;
        dealData['dealName'] = deal.name;
        dealData['invStatus'] = deal.realizationType;
        dealData['strategy'] = deal[cfg.classification.field];
        dealData['vintageYear'] = deal.vintageYear;
        dealData['valuationDateFormatted'] = this.dateFormatPipe.transform(deal.valuationDate, 'M/d/yy');
        dealData['dealCommitment'] = deal.commitment;
        dealData['dealReportedContributions'] = deal.reportedContributions;
        dealData['dealContributions'] = deal.contributions;
        dealData['dealRemainingCommitment'] = deal.remainingCommitment;
        dealData['dealReportedDistributions'] = deal.reportedDistributions;
        dealData['dealDistributions'] = deal.distributions;
        dealData['dealReportedValue'] = deal.reportedValue;
        dealData['dealSubsequentContributions'] = deal.subsequentContributions;
        dealData['dealSubsequentDistributions'] = deal.subsequentDistributions;
        dealData['dealFxGainLoss'] = deal.fxGainLoss;
        dealData['dealNav'] = deal.nav;
        dealData['dealTotalValue'] = deal.totalValue;
        dealData['dealTVPI'] = this.tvpiValueFormatPipe.transform(deal.tvpi);
        dealData['dealIRR'] = this.irrValueFormatPipe.transform(deal.irr);
        seriesDealsArray.push(dealData);

        const sortedHoldings = this.exportCsvService.sortArrayAscending(deal.holdings);
        sortedHoldings.forEach((holding: DealHierarchy['holdings'][number]) => {
          const data: Record<string, unknown> = {};
          data['invType'] = invType.name;
          data['dealName'] = deal.name;
          data['holdingName'] = holding.name;
          data['invStatus'] = deal.realizationType;
          data['strategy'] = deal[cfg.classification.field];
          data['vintageYear'] = deal.vintageYear;
          data['valuationDateFormatted'] = this.dateFormatPipe.transform(holding.valuationDate ?? '', 'M/d/yy');
          data['holdingCommitment'] = holding.commitment;
          data['holdingReportedContributions'] = holding.reportedContributions;
          data['holdingContributions'] = holding.contributions;
          data['holdingRemainingCommitment'] = holding.remainingCommitment;
          data['holdingReportedDistributions'] = holding.reportedDistributions;
          data['holdingDistributions'] = holding.distributions;
          data['holdingReportedValue'] = holding.reportedValue;
          data['holdingSubsequentContributions'] = holding.subsequentContributions;
          data['holdingSubsequentDistributions'] = holding.subsequentDistributions;
          data['holdingFxGainLoss'] = holding.fxGainLoss;
          data['holdingNav'] = holding.nav;
          data['holdingTotalValue'] = holding.totalValue;
          seriesHoldingsArray.push(data);
        })
      })
    })
    const worksheets = [{name: 'Series Deals', csvArray: seriesDealsArray}, { name: 'Series Holdings', csvArray: seriesHoldingsArray }, { name: 'Investment Type Subtotals', csvArray: invTypeArray }]
    this.exportCsvService.exportToExcelFile(filename, worksheets);
    (event.target as HTMLButtonElement).disabled = false;
  }

  updateColumns(expandView: boolean) {
    this.expandView = expandView;
    this.activityTracking.tableExpandViewToggle('series holding table', this.seriesName(), expandView);
    const cfg = this.seriesTableConfig.config();
    this.columnsToDisplay = expandView ? cfg.deals.expandedColumns : cfg.deals.columns;
  }

  scrollTableHandler(event: Event, investmentType: InvestmentTypeHierarchy) {
    if ((event.target as HTMLElement).scrollLeft > 0) {
      investmentType['displayShadowOnTable'] = true;
    } else {
      investmentType['displayShadowOnTable'] = false;
    }
  }

  expandTable(investmentType: InvestmentTypeHierarchy) {
    investmentType.isExpanded = !investmentType.isExpanded;
    if (investmentType.isExpanded) {
      this.sortingInitialized = false;
      this.initializeSorting();
    }
    this.activityTracking.expandOrCollapseToggled('investment', investmentType, 'series holdings table', investmentType.isExpanded)
  }
}
